/**
 * Wave 27.0 — Projects list.
 *
 * Phone-style card grid (one card per project). Tap card → /projects/:id
 * (full-page detail editor). Filter chips by status. Customer scope and
 * search.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search, X, Plus, Briefcase, Loader2, AlertTriangle,
  TrendingUp, TrendingDown, Activity, Inbox, ChevronRight, Sparkles,
  LayoutGrid, List,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

const STATUS_COLORS = {
  planning: "#A78BFA",
  active: "#10B981",
  on_hold: "#F59E0B",
  complete: "#0EA5E9",
  archived: "#647087",
  cancelled: "#F43F5E",
};

const STATUS_LABEL = {
  planning: "Planning",
  active: "Active",
  on_hold: "On hold",
  complete: "Complete",
  archived: "Archived",
  cancelled: "Cancelled",
};

const fmtMoney = (n, ccy = "ZAR") =>
  n == null
    ? "—"
    : `${ccy} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Projects() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const isTech = role === "technician";
  // Defensive: don't render cost columns until the auth context confirms
  // a non-tech role (covers the brief bootstrap window when user is null).
  const canSeeCosts = !!user && !isTech;
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  // Iter 427.p35 — grid | list view. Persisted per-user in localStorage
  // so returning to the page keeps the operator's preferred density.
  const [view, setView] = useState(() => {
    try {
      const v = localStorage.getItem("projects.view");
      return v === "list" ? "list" : "grid";
    } catch { return "grid"; }
  });
  useEffect(() => {
    try { localStorage.setItem("projects.view", view); } catch { /* ignore */ }
  }, [view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (q) params.q = q;
      if (status) params.status = status;
      const { data } = await api.get("/projects", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Failed to load projects.");
    } finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (status) next.status = status;
    setSearchParams(next, { replace: true });
  }, [q, status, setSearchParams]);

  return (
    <div className="space-y-4" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Projects</h1>
          <p className="text-xs text-[#647087] mt-0.5">
            {total.toLocaleString()} project{total === 1 ? "" : "s"} — every quote, job, invoice and expense in one place.
          </p>
        </div>
        {!isTech && (
          <div className="flex items-center gap-2">
            {/* Iter 427.p35 — grid ↔ list view toggle */}
            <div className="inline-flex rounded-md border border-white/15 overflow-hidden" data-testid="projects-view-toggle">
              <button
                type="button"
                onClick={() => setView("grid")}
                className={`px-2 py-1 text-xs inline-flex items-center gap-1 ${view === "grid" ? "bg-[#1FB6C1] text-[#0a0b0e]" : "text-[#a0aabf] hover:bg-white/5"}`}
                data-testid="projects-view-grid"
                title="Grid view"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setView("list")}
                className={`px-2 py-1 text-xs inline-flex items-center gap-1 border-l border-white/15 ${view === "list" ? "bg-[#1FB6C1] text-[#0a0b0e]" : "text-[#a0aabf] hover:bg-white/5"}`}
                data-testid="projects-view-list"
                title="List view"
              >
                <List className="w-3.5 h-3.5" />
              </button>
            </div>
            <Link to="/projects/workbench" className="hv-btn hv-btn-ghost text-xs" data-testid="projects-workbench-btn">
              <Sparkles className="w-3.5 h-3.5" /> Workbench
            </Link>
            <Link to="/timeline" className="hv-btn hv-btn-ghost text-xs" data-testid="projects-timeline-btn">
              <Activity className="w-3.5 h-3.5" /> Timeline
            </Link>
            <Link to="/projects/new" className="hv-btn hv-btn-primary text-xs" data-testid="projects-new-btn">
              <Plus className="w-3.5 h-3.5" /> New project
            </Link>
          </div>
        )}
      </div>

      {/* Phase A.5 — Risk Register strip */}
      {canSeeCosts && <RiskRegisterStrip />}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <FilterChip label="All" active={!status} onClick={() => setStatus("")} testId="proj-filter-all" />
        {Object.keys(STATUS_LABEL).filter((k) => k !== "archived").map((s) => (
          <FilterChip
            key={s}
            label={STATUS_LABEL[s]}
            active={status === s}
            color={STATUS_COLORS[s]}
            onClick={() => setStatus(s)}
            testId={`proj-filter-${s}`}
          />
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, ref, tag…"
          className="hv-input text-xs pl-8 pr-7 w-full"
          data-testid="projects-search-input"
        />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087]" aria-label="Clear">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="hv-card h-[140px] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="hv-card p-10 text-center text-[#647087] text-sm" data-testid="projects-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <div>No projects {status ? `with status "${STATUS_LABEL[status]}"` : "yet"}.</div>
          {!isTech && <div className="text-xs mt-1">Tap <span className="text-[#1FB6C1]">New project</span> to start.</div>}
        </div>
      ) : (
        view === "list" ? (
          <div className="flex flex-col gap-2" data-testid="projects-list">
            {items.map((p) => (
              <ProjectRow key={p.id} p={p} canSeeCosts={canSeeCosts} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="projects-grid">
            {items.map((p) => (
              <ProjectCard key={p.id} p={p} canSeeCosts={canSeeCosts} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function FilterChip({ label, active, color, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1 border transition-colors ${
        active
          ? "border-transparent text-[#0a0b0e] font-semibold"
          : "border-white/15 text-[#a0aabf] hover:bg-white/5"
      }`}
      style={active ? { background: color || "#1FB6C1" } : {}}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function ProjectCard({ p, canSeeCosts }) {
  const burn = p.budget_burn_pct;
  const burnColor =
    burn == null ? "#647087"
    : burn < 70 ? "#10B981"
    : burn < 90 ? "#F59E0B"
    : burn < 100 ? "#F43F5E"
    : "#DC2626";
  const margin = p.margin_amount;
  // Iter 427.p35 — Spent must reflect WIP (POs + procurement + expenses).
  // `expense_total` alone is the approved-expenses-only figure and reads
  // as ZAR 0 on projects that spend via POs / procurement.
  const spent = p.tile_spent ?? p.wip_total ?? p.cost_total ?? p.expense_total ?? 0;
  return (
    <Link
      to={`/projects/${p.id}`}
      className="hv-card !p-3 hover:border-[#1FB6C1]/50 transition-colors flex flex-col gap-2 group"
      data-testid={`project-card-${p.id}`}
      style={{ borderLeftColor: p.color || "#1FB6C1", borderLeftWidth: "3px" }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono text-[#647087]">{p.short_id}</span>
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold"
              style={{ background: `${STATUS_COLORS[p.status]}22`, color: STATUS_COLORS[p.status] }}
            >
              {STATUS_LABEL[p.status] || p.status}
            </span>
          </div>
          <div className="font-display font-semibold text-sm text-white mt-0.5 truncate">{p.title}</div>
          <div className="text-[11px] text-[#a0aabf] truncate">{p.customer_name || "—"}</div>
        </div>
        <Briefcase className="w-5 h-5 text-[#647087] group-hover:text-[#1FB6C1] shrink-0" />
      </div>
      {canSeeCosts && (
        <div className="mt-1 grid grid-cols-3 gap-1.5 text-[10px]" data-testid="project-card-financials">
          <div>
            <div className="text-[#647087] uppercase tracking-wider">Budget</div>
            <div className="font-mono text-white">{fmtMoney(p.effective_budget, p.budget_currency)}</div>
          </div>
          <div>
            <div className="text-[#647087] uppercase tracking-wider">Spent</div>
            <div className="font-mono" style={{ color: burnColor }}>
              {fmtMoney(spent, p.budget_currency)}
            </div>
          </div>
          <div>
            <div className="text-[#647087] uppercase tracking-wider">Margin</div>
            <div className="font-mono inline-flex items-center gap-0.5" style={{ color: (margin ?? 0) >= 0 ? "#10B981" : "#F43F5E" }}>
              {(margin ?? 0) >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
              {fmtMoney(margin, p.budget_currency)}
            </div>
          </div>
        </div>
      )}
      {canSeeCosts && p.effective_budget > 0 && (
        <div className="mt-1">
          <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${Math.min(100, burn || 0)}%`, background: burnColor }}
            />
          </div>
          <div className="flex items-center justify-between text-[9px] text-[#647087] mt-0.5">
            <span>{burn != null ? `${burn}% spent` : "—"}</span>
            {p.pending_expenses_count > 0 && (
              <span className="text-amber-400 inline-flex items-center gap-0.5">
                <AlertTriangle className="w-2.5 h-2.5" />
                {p.pending_expenses_count} pending review
              </span>
            )}
          </div>
        </div>
      )}
      {/* Iter 427.p35 — At-a-glance movement chip */}
      {p.updated_at && (
        <div className="text-[9px] text-[#647087] font-mono">
          Last activity {relTime(p.updated_at)}
        </div>
      )}
    </Link>
  );
}

/** Iter 427.p35 — Compact relative-time formatter for at-a-glance movement. */
function relTime(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo ago`;
    return `${Math.floor(diff / 31536000)}y ago`;
  } catch { return ""; }
}

/** Iter 427.p35 — Dense list-row for the alternative list-view. Same
 *  data density as the card but stacks horizontally so many projects
 *  fit on one screen. Tap = navigate to detail. */
function ProjectRow({ p, canSeeCosts }) {
  const burn = p.budget_burn_pct;
  const burnColor =
    burn == null ? "#647087"
    : burn < 70 ? "#10B981"
    : burn < 90 ? "#F59E0B"
    : burn < 100 ? "#F43F5E"
    : "#DC2626";
  const margin = p.margin_amount;
  const spent = p.tile_spent ?? p.wip_total ?? p.cost_total ?? p.expense_total ?? 0;
  return (
    <Link
      to={`/projects/${p.id}`}
      className="hv-card !p-2.5 hover:border-[#1FB6C1]/50 transition-colors grid grid-cols-12 gap-2 items-center group text-[11px]"
      data-testid={`project-row-${p.id}`}
      style={{ borderLeftColor: p.color || "#1FB6C1", borderLeftWidth: "3px" }}
    >
      <div className="col-span-4 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-[#647087]">{p.short_id}</span>
          <span
            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold"
            style={{ background: `${STATUS_COLORS[p.status]}22`, color: STATUS_COLORS[p.status] }}
          >
            {STATUS_LABEL[p.status] || p.status}
          </span>
        </div>
        <div className="font-display font-semibold text-sm text-white mt-0.5 truncate">{p.title}</div>
        <div className="text-[10px] text-[#a0aabf] truncate">{p.customer_name || "—"}</div>
      </div>
      {canSeeCosts ? (
        <>
          <div className="col-span-2">
            <div className="text-[#647087] uppercase tracking-wider text-[9px]">Budget</div>
            <div className="font-mono text-white">{fmtMoney(p.effective_budget, p.budget_currency)}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[#647087] uppercase tracking-wider text-[9px]">Spent</div>
            <div className="font-mono" style={{ color: burnColor }}>
              {fmtMoney(spent, p.budget_currency)}
            </div>
          </div>
          <div className="col-span-2">
            <div className="text-[#647087] uppercase tracking-wider text-[9px]">Margin</div>
            <div className="font-mono inline-flex items-center gap-0.5" style={{ color: (margin ?? 0) >= 0 ? "#10B981" : "#F43F5E" }}>
              {(margin ?? 0) >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
              {fmtMoney(margin, p.budget_currency)}
            </div>
          </div>
          <div className="col-span-2 flex flex-col items-end gap-0.5">
            {p.effective_budget > 0 && (
              <div className="w-full h-1 rounded-full bg-white/[0.06] overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{ width: `${Math.min(100, burn || 0)}%`, background: burnColor }}
                />
              </div>
            )}
            <div className="flex items-center justify-end gap-2 w-full text-[9px] text-[#647087]">
              <span>{burn != null ? `${burn}%` : "—"}</span>
              {p.pending_expenses_count > 0 && (
                <span className="text-amber-400 inline-flex items-center gap-0.5">
                  <AlertTriangle className="w-2.5 h-2.5" />
                  {p.pending_expenses_count}
                </span>
              )}
              {p.updated_at && <span className="font-mono">{relTime(p.updated_at)}</span>}
            </div>
          </div>
        </>
      ) : (
        <div className="col-span-8 text-[10px] text-[#647087]">
          {p.updated_at && <span className="font-mono">Last activity {relTime(p.updated_at)}</span>}
        </div>
      )}
    </Link>
  );
}


/** Phase A.5 — Risk Register strip on /projects. Shows up to 5 projects
 *  ranked by days_to_overrun ascending (most urgent first). Hidden if no
 *  risks are detected. Manager/admin/owner only.
 */
function RiskRegisterStrip() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/insights/risk-register")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return null;
  if (items.length === 0) return null;

  const top = items.slice(0, 5);
  return (
    <div
      className="hv-card !p-3 border-amber-400/30 bg-gradient-to-br from-amber-500/5 to-transparent"
      data-testid="projects-risk-strip"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-sm inline-flex items-center gap-1.5 text-amber-300">
          <AlertTriangle className="w-3.5 h-3.5" /> Risk register
          <span className="text-[10px] text-[#647087] font-mono">({items.length})</span>
        </div>
        <span className="text-[10px] text-[#647087]">Most urgent first</span>
      </div>
      <ul className="space-y-1">
        {top.map((r) => {
          const c =
            r.status_band === "over" ? "#DC2626"
            : r.status_band === "warning" ? "#F43F5E"
            : r.status_band === "caution" ? "#F59E0B"
            : "#22C55E";
          return (
            <li key={r.id}>
              <Link
                to={`/projects/${r.id}`}
                className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded hover:bg-white/[0.03]"
                data-testid={`risk-row-${r.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold"
                    style={{ background: `${c}22`, color: c }}
                  >
                    {r.status_band}
                  </span>
                  <span className="font-mono text-[10px] text-[#647087]">{r.short_id}</span>
                  <span className="text-white truncate">{r.title}</span>
                </div>
                <div className="text-right shrink-0 flex items-center gap-3">
                  <div>
                    <div className="font-mono text-xs" style={{ color: c }}>{r.burn_pct}%</div>
                    {r.days_to_overrun != null && r.days_to_overrun < 9999 && (
                      <div className="text-[9px] text-[#647087]">
                        {r.days_to_overrun <= 0 ? "Over" : `${r.days_to_overrun}d to overrun`}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-3 h-3 text-[#647087]" />
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
